<script setup lang="ts">
import Button from "@/components/Reusables/Buttons/Button.vue";
import NumberInput from "@/components/Reusables/Inputs/NumberInput.vue";
import SelectInputWithKeyValue from "@/components/Reusables/Inputs/SelectInputWithKeyValue.vue";
import type { WasteMaterial } from "@/api/services/waste/types";
import type { WasteTypeGroup } from "@/pages/WasteSorting/types/types";
import { useProjectEditReadOnly } from "@/pages/ProjectVersionHistory/composables/useProjectEditReadOnly.composable";
import RevisionGatedButton from "@/pages/ProjectVersionHistory/components/revisions/RevisionGatedButton.vue";
import { useModalStore } from "@/stores/modals/modals.store";
import format from "@/services/Utils/format";
import { computed, ref } from "vue";
import type { MaterialWasteSourceFragment } from "@/graphql";
import UnitId from "@/features/ConstructionAmountCalculator/constants/UnitId";

const props = defineProps<{
  material: WasteMaterial;
  group: WasteTypeGroup;
  onSave: (weight: number) => Promise<void>;
  materialWeight?: number;
  initialPercentage?: number;
  wasteSource?: MaterialWasteSourceFragment;
  weightUnit?: "kg" | "ton";
}>();

const isPercentageLocked = computed(() => !props.group.isCustom && props.wasteSource?.unit?.id === UnitId.PERCENTAGE);
const lockedPercentage = computed(() => (isPercentageLocked.value ? props.group.wasteType.value : null));

const modals = useModalStore();

// Both ways into this modal — the drag and the Assign menu — are already blocked for a Viewer and
// while a frozen revision is previewed, so this covers the modal that was open when the guard
// changed: its save would be refused by Hasura, or would create a mapping on the current revision
// under an older save point's name.
const { isReadOnly: isRevisionReadOnly, blockedByPreview } = useProjectEditReadOnly();

// Weights are stored in kg; the page-level kg/ton switch controls how we display them here.
const isTon = computed(() => props.weightUnit === "ton");
const unitLabel = computed(() => (isTon.value ? "ton" : "kg"));
const toDisplay = (kg: number) => (isTon.value ? kg / 1000 : kg);
const toKg = (display: number) => (isTon.value ? display * 1000 : display);

const percentage = ref<number | null>(isPercentageLocked.value ? lockedPercentage.value : props.initialPercentage ?? null);
const weight = ref<number | null>(
  (() => {
    const pct = isPercentageLocked.value ? lockedPercentage.value : props.initialPercentage;
    return pct && props.materialWeight ? parseFloat(toDisplay((pct / 100) * props.materialWeight).toFixed(2)) : null;
  })()
);
const isSaving = ref(false);
const unitOptions = computed(() => [{ name: unitLabel.value, value: unitLabel.value }]);
const selectedUnit = ref(unitLabel.value);
const displayMaterialWeight = computed(() => (props.materialWeight !== undefined ? toDisplay(props.materialWeight) : undefined));

const saveDisabled = computed(() => !weight.value || weight.value <= 0);

const handleWeightChange = (newWeight: number | null) => {
  weight.value = newWeight;
  if (props.materialWeight && props.materialWeight > 0 && newWeight !== null) {
    percentage.value = parseFloat(((toKg(newWeight) / props.materialWeight) * 100).toFixed(2));
  } else {
    percentage.value = null;
  }
};

const handlePercentageChange = (newPercentage: number | null) => {
  percentage.value = newPercentage;
  if (props.materialWeight && props.materialWeight > 0 && newPercentage !== null) {
    weight.value = parseFloat(toDisplay((newPercentage / 100) * props.materialWeight).toFixed(2));
  } else {
    weight.value = null;
  }
};

const handleSave = async () => {
  if (blockedByPreview()) return;
  if (saveDisabled.value || !weight.value) return;
  isSaving.value = true;
  try {
    await props.onSave(toKg(weight.value));
    modals.closeByName("WasteMaterialDropModal");
  } finally {
    isSaving.value = false;
  }
};

const handleCancel = () => {
  modals.closeByName("WasteMaterialDropModal");
};
</script>

<template>
  <div class="flex h-full flex-col gap-4">
    <h2 class="t-sh4">Add waste sorting to {{ group.wasteType.label }}</h2>

    <p class="text-type-muted t-small">
      How much waste of <strong>{{ material.name }}</strong> should be added to <strong>{{ group.wasteType.label }}</strong
      >?
    </p>

    <div
      class="grid grow gap-3"
      :class="materialWeight !== undefined ? 'grid-cols-[1fr_auto_auto_auto]' : 'grid-cols-[1fr_auto]'">
      <NumberInput
        :value="weight"
        label="Weight"
        placeholder="0"
        color="primary"
        :positive="true"
        :max="displayMaterialWeight"
        :disabled="isPercentageLocked || isRevisionReadOnly"
        @update:value="handleWeightChange" />
      <SelectInputWithKeyValue
        :value="selectedUnit"
        :options="unitOptions"
        label="Unit"
        :is-disabled="isRevisionReadOnly"
        @update:value="selectedUnit = $event" />
      <div
        v-if="materialWeight !== undefined"
        class="flex h-10 items-center self-end">
        <div class="h-6 w-px bg-slate-200"></div>
      </div>
      <NumberInput
        v-if="materialWeight !== undefined"
        :value="percentage"
        label="Percentage"
        placeholder="0"
        color="primary"
        :max="100"
        :positive="true"
        :max-decimals="2"
        :disabled="isPercentageLocked || isRevisionReadOnly"
        class="text-slate-600"
        @update:value="handlePercentageChange" />
    </div>
    <div
      v-if="materialWeight !== undefined"
      class="t-xs flex items-center justify-end py-2 text-slate-500">
      <span
        >Total material weight:
        <strong class="text-slate-700">{{ format.decimalNumber(displayMaterialWeight ?? 0, 2) }} {{ unitLabel }}</strong></span
      >
    </div>

    <div class="mt-auto flex justify-between">
      <Button
        label="Cancel"
        color="button"
        :disabled="isSaving"
        @click="handleCancel" />
      <RevisionGatedButton
        :label="isSaving ? 'Saving...' : 'Save'"
        :color="saveDisabled || isSaving ? 'disabled' : 'primary'"
        :disabled="saveDisabled || isSaving"
        :is-loading="isSaving"
        :guard="useProjectEditReadOnly"
        @click="handleSave" />
    </div>
  </div>
</template>
